import React, { useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Truck, ScanLine, ShieldCheck, Loader2 } from "lucide-react";
import { destinoTrasLogin } from "@/auth/destinos";

const DEMO_USERS = [
  { rol: "admin",     email: "admin@deca.es",     password: "Admin.2026",     label: "Admin" },
  { rol: "empresa",   email: "empresa@deca.es",   password: "Empresa.2026",   label: "Empresa" },
  { rol: "gestoria",  email: "gestoria@deca.es",  password: "Gestoria.2026",  label: "Gestoría" },
  { rol: "conductor", email: "conductor@deca.es", password: "Conductor.2026", label: "Conductor" },
];

export default function LoginPage() {
  const { login, error, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) {
    const dest = destinoTrasLogin(user.rol);
    setTimeout(() => nav(dest, { replace: true }), 0);
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const logged = await login(email, password);
    setLoading(false);
    if (logged) nav(destinoTrasLogin(logged.rol), { replace: true });
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-slate-50">
      {/* Left: cover image */}
      <div className="relative hidden md:block min-h-screen bg-slate-950">
        <div
          className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(29,78,216,0.35),_transparent_55%)]" aria-hidden="true" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-white/10 border border-white/20 flex items-center justify-center">
              <Truck className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-white/70">DeCA · Web</div>
              <div className="text-lg font-semibold" style={{ fontFamily: "Chivo" }}>Control administrativo del transporte</div>
            </div>
          </div>

          <div className="max-w-md">
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight" style={{ fontFamily: "Chivo" }}>
              El documento electrónico obligatorio del transporte,<br/>listo para inspección.
            </h1>
            <p className="mt-4 text-white/70 text-base leading-relaxed">
              Selecciona albaranes, agrupa por cargador y transportista y emite el DeCA con PDF nativo y QR de descarga directa. Conforme a la Resolución de 5/6/2026.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
              <div className="flex items-start gap-2"><ScanLine className="h-4 w-4 mt-0.5 text-emerald-300" strokeWidth={1.75} /><div><b>QR nativo</b><div className="text-white/60 text-xs">URL directa al PDF</div></div></div>
              <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-300" strokeWidth={1.75} /><div><b>Trazabilidad</b><div className="text-white/60 text-xs">Versión + auditoría</div></div></div>
              <div className="flex items-start gap-2"><Truck className="h-4 w-4 mt-0.5 text-emerald-300" strokeWidth={1.75} /><div><b>Modo conductor</b><div className="text-white/60 text-xs">Offline, en la ruta</div></div></div>
            </div>
          </div>

          <div className="text-xs text-white/50">BOE-A-2026-12784 · Obligatorio desde 5/10/2026</div>
        </div>
      </div>

      {/* Right: login card */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md p-8 border-slate-200">
          <div className="mb-6">
            <div className="label-tag mb-2">Acceso</div>
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Entra a DeCA Web</h2>
            <p className="text-sm text-slate-500 mt-1">Introduce tus credenciales para acceder al panel.</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="label-tag">Correo</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1"/>
            </div>
            <div>
              <Label htmlFor="password" className="label-tag">Contraseña</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1"/>
            </div>
            {error ? (<div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded px-3 py-2" data-testid="login-error">{error}</div>) : null}
            <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white tap-44" data-testid="login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Entrar
            </Button>
          </form>

          <div className="mt-8 hidden" hidden aria-hidden="true">
            <div className="label-tag mb-2">Cuentas demo (un toque)</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_USERS.map((u) => (
                <button
                  key={u.rol}
                  type="button"
                  onClick={() => { setEmail(u.email); setPassword(u.password); }}
                  className="border border-slate-200 rounded-md px-3 py-2 text-left hover:border-blue-600 hover:bg-blue-50 transition-colors"
                  data-testid={`demo-${u.rol}`}
                >
                  <div className="text-sm font-semibold text-slate-900">{u.label}</div>
                  <div className="text-[11px] font-mono text-slate-500 truncate">{u.email}</div>
                </button>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
